import React, { useState, useEffect, useCallback } from "react";
import { customerApi } from "../../api/client";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import { IoChevronDownOutline, IoChevronUpOutline, IoPrintOutline } from "react-icons/io5";

function formatDate(str) {
  if (!str) return "—";
  try {
    const s = String(str).slice(0, 10);
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return str;
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-IN", {
      day: "numeric", month: "short", year: "numeric", timeZone: "UTC",
    });
  } catch (_) {
    return str;
  }
}

function formatInr(paise) {
  const n = Number(paise);
  if (!Number.isFinite(n)) return "—";
  return `₹${(n / 100).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const STATUS_COLORS = {
  pending_payment: "warning",
  confirmed: "info",
  out_for_delivery: "primary",
  delivered: "success",
  returned: "default",
  cancelled: "error",
  refunded: "default",
};

const STATUS_LABELS = {
  pending_payment: "Pending Payment",
  confirmed: "Confirmed",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  returned: "Returned",
  cancelled: "Cancelled",
  refunded: "Refunded",
};

function OrderDetail({ orderId }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    customerApi(`/api/customer/orders/${orderId}`)
      .then(setDetail)
      .catch((e) => setError(e.message || "Failed to load order details"))
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading) return <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}><CircularProgress size={24} /></Box>;
  if (error) return <Box sx={{ p: 2 }}><Typography color="error" variant="body2">{error}</Typography></Box>;
  if (!detail) return null;

  const addr = [
    detail.address_line1,
    detail.address_line2,
    detail.address_city,
    detail.address_state,
    detail.address_pincode,
  ].filter(Boolean).join(", ");

  const handlePrint = () => {
    const items = Array.isArray(detail.items) ? detail.items : [];
    const rows = items
      .map(
        (it) =>
          `<tr><td>${it.product_name || `#${it.product_id}`}</td><td style="text-align:center">${it.quantity} × ${it.rent_days || detail.rent_days || 1}d</td><td style="text-align:right">${it.unit_paise ? formatInr(it.unit_paise * it.quantity) : "—"}</td></tr>`
      )
      .join("");
    const win = window.open("", "_blank", "width=700,height=900");
    if (!win) {
      alert("Pop-up was blocked. Please allow pop-ups for this site to print receipts.");
      return;
    }
    win.document.write(`<!DOCTYPE html><html><head><title>Receipt #${detail.id}</title>
<style>body{font-family:Segoe UI,sans-serif;font-size:14px;color:#111;padding:24px;max-width:600px;margin:0 auto}
h2{margin-bottom:4px}table{width:100%;border-collapse:collapse;margin:12px 0}
th,td{padding:6px;border-bottom:1px solid #eee}th{text-align:left;color:#555}
.total{font-weight:700}.footer{color:#888;font-size:12px;margin-top:20px}</style></head>
<body onload="window.print()">
<h2>Gem Fashion Studios</h2><p style="color:#555">GEM House, Andheri West, Mumbai</p>
<hr/>
<p><strong>Order #${detail.id}</strong> &nbsp;·&nbsp; ${formatDate(detail.created_at)}</p>
<p>Event: <strong>${formatDate(detail.event_date)}</strong> &nbsp; Return by: <strong>${formatDate(detail.return_by)}</strong></p>
<p>${addr ? `Delivery: ${addr}` : ""}</p>
<table><thead><tr><th>Item</th><th style="text-align:center">Qty × Days</th><th style="text-align:right">Amount</th></tr></thead>
<tbody>${rows}</tbody></table>
${Number(detail.discount_paise) > 0 ? `<p>Discount: −${formatInr(detail.discount_paise)}</p>` : ""}
${Number(detail.shipping_paise) > 0 ? `<p>Shipping: ${formatInr(detail.shipping_paise)}</p>` : ""}
${Number(detail.gst_paise) > 0 ? `<p>GST: ${formatInr(detail.gst_paise)}</p>` : ""}
${Number(detail.deposit_paise) > 0 ? `<p>Security deposit: ${formatInr(detail.deposit_paise)}</p>` : ""}
<p class="total">Total: ${formatInr(detail.total_paise)}</p>
<div class="footer">Thank you for choosing Gem Fashion Studios. This is an informal receipt, not a tax invoice.</div>
</body></html>`);
    win.document.close();
  };

  const items = Array.isArray(detail.items) ? detail.items : [];

  return (
    <Box sx={{ p: 2, bgcolor: "grey.50", borderTop: "1px solid", borderColor: "divider" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button size="small" startIcon={<IoPrintOutline />} onClick={handlePrint} sx={{ textTransform: "none" }}>
          Print receipt
        </Button>
      </Box>
      <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mb: 2 }}>
        {/* Items */}
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Items</Typography>
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No item details available.</Typography>
          ) : (
            items.map((item) => (
              <Box key={item.id} sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                <Typography variant="body2">
                  {item.product_name || `Product #${item.product_id}`}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    × {item.quantity} · {item.rent_days || detail.rent_days || 1} days
                  </Typography>
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {item.unit_paise ? formatInr(item.unit_paise * item.quantity) : "—"}
                </Typography>
              </Box>
            ))
          )}
        </Box>

        {/* Totals */}
        <Box sx={{ minWidth: 180 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Charges</Typography>
          {Number(detail.discount_paise) > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">Discount</Typography>
              <Typography variant="body2" color="success.main">−{formatInr(detail.discount_paise)}</Typography>
            </Box>
          )}
          {Number(detail.shipping_paise) > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">Shipping</Typography>
              <Typography variant="body2">{formatInr(detail.shipping_paise)}</Typography>
            </Box>
          )}
          {Number(detail.gst_paise) > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">GST</Typography>
              <Typography variant="body2">{formatInr(detail.gst_paise)}</Typography>
            </Box>
          )}
          {Number(detail.deposit_paise) > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">Deposit</Typography>
              <Typography variant="body2">{formatInr(detail.deposit_paise)}</Typography>
            </Box>
          )}
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" fontWeight={700}>Total</Typography>
            <Typography variant="body2" fontWeight={700}>{formatInr(detail.total_paise)}</Typography>
          </Box>
        </Box>

        {/* Delivery / contact */}
        <Box sx={{ minWidth: 200 }}>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Delivery</Typography>
          {addr && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{addr}</Typography>}
          {detail.contact_phone && (
            <Typography variant="body2" color="text.secondary">
              {detail.contact_phone}
            </Typography>
          )}
          {detail.return_by && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Return by:</strong> {formatDate(detail.return_by)}
            </Typography>
          )}
          {detail.payment_method && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Payment: {String(detail.payment_method).toUpperCase()}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function AccountOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    customerApi("/api/customer/orders")
      .then(setOrders)
      .catch(() => setError("Failed to load orders"))
      .finally(() => setLoading(false));
  }, []);

  const toggleExpand = useCallback((id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        My Orders
      </Typography>
      {orders.length === 0 ? (
        <Typography color="text.secondary">You have no orders yet.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                <TableCell sx={{ fontWeight: 600, width: 40 }} />
                <TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Placed</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Event date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Return by</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((o) => (
                <React.Fragment key={o.id}>
                  <TableRow
                    hover
                    sx={{ cursor: "pointer", "& > *": { borderBottom: expandedId === o.id ? "none" : undefined } }}
                    onClick={() => toggleExpand(o.id)}
                  >
                    <TableCell padding="checkbox">
                      <IconButton size="small" aria-label={expandedId === o.id ? "Collapse" : "Expand"}>
                        {expandedId === o.id ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
                      </IconButton>
                    </TableCell>
                    <TableCell>#{o.id}</TableCell>
                    <TableCell>{formatDate(o.created_at)}</TableCell>
                    <TableCell>{formatDate(o.event_date)}</TableCell>
                    <TableCell>{formatDate(o.return_by)}</TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[o.status] || o.status}
                        color={STATUS_COLORS[o.status] || "default"}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{formatInr(o.total_paise)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                      <Collapse in={expandedId === o.id} timeout="auto" unmountOnExit>
                        <OrderDetail orderId={o.id} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
